import type { AgentAssetAttachment } from '@nest-react/domain';
import {
  type PropsWithChildren,
  type ReactElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { AgentReferenceImage } from './AgentReferenceImageList';

export type AgentLaunchDraft = {
  token: string;
  workspaceId: string;
  text: string;
  activeSkillId: string | null;
  referenceImages: AgentReferenceImage[];
  assetAttachments: AgentAssetAttachment[];
};

type AgentLaunchDraftValue = {
  setDraft: (draft: AgentLaunchDraft) => void;
  peekDraft: () => AgentLaunchDraft | null;
  acknowledgeDraft: (workspaceId: string, token: string) => boolean;
  discardDraft: () => void;
};

const revokeDraftPreviews = (draft: AgentLaunchDraft): void => {
  for (const image of draft.referenceImages) {
    if (image.file && image.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(image.previewUrl);
    }
  }
};

const AgentLaunchDraftContext = createContext<AgentLaunchDraftValue | null>(
  null
);

export const AgentLaunchDraftProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  const draftRef = useRef<AgentLaunchDraft | null>(null);
  const consumedTokensRef = useRef(new Set<string>());

  const value = useMemo<AgentLaunchDraftValue>(
    () => ({
      setDraft: draft => {
        if (draftRef.current) revokeDraftPreviews(draftRef.current);
        draftRef.current = draft;
      },
      peekDraft: () => draftRef.current,
      acknowledgeDraft: (workspaceId, token) => {
        const draft = draftRef.current;
        if (
          !draft ||
          draft.token !== token ||
          consumedTokensRef.current.has(token)
        ) {
          return false;
        }
        draftRef.current = null;
        consumedTokensRef.current.add(token);
        if (draft.workspaceId !== workspaceId) {
          revokeDraftPreviews(draft);
          return false;
        }
        return true;
      },
      discardDraft: () => {
        if (draftRef.current) revokeDraftPreviews(draftRef.current);
        draftRef.current = null;
      },
    }),
    []
  );

  useEffect(
    () => () => {
      if (draftRef.current) revokeDraftPreviews(draftRef.current);
    },
    []
  );

  return (
    <AgentLaunchDraftContext.Provider value={value}>
      {children}
    </AgentLaunchDraftContext.Provider>
  );
};

export const useAgentLaunchDraft = (): AgentLaunchDraftValue => {
  const value = useContext(AgentLaunchDraftContext);
  if (!value) throw new Error('AgentLaunchDraftProvider is missing');
  return value;
};
